import React from 'react';
import styled, { keyframes } from 'styled-components';
import { MobileAppFlair } from 'types/graphql';

type LoadingFlairPreviewProps = {
  flairType: MobileAppFlair;
  color: string;
};

/** Renders a live CSS-animated preview of the mobile app loading indicator for the given decoration type. */
export function LoadingFlairPreview({ flairType, color }: LoadingFlairPreviewProps): JSX.Element {
  return (
    <Container aria-hidden='true'>
      {flairType === MobileAppFlair.none && <LoadingText>Loading...</LoadingText>}
      {flairType === MobileAppFlair.angled && <AngledBar $color={color} />}
      {flairType === MobileAppFlair.curved && <CurvedDot $color={color} />}
      {flairType === MobileAppFlair.jagged && <JaggedSquare $color={color} />}
      {flairType === MobileAppFlair.wavy && (
        <WavyDots>
          <WavyDot $color={color} $delay={0} />
          <WavyDot $color={color} $delay={0.15} />
          <WavyDot $color={color} $delay={0.3} />
        </WavyDots>
      )}
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  height: 48px;
  justify-content: center;
  margin-top: 8px;
  width: 100%;
`;

const LoadingText = styled.span`
  color: ${({ theme }) => theme.colors.grey[50]};
  font-size: 14px;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const AngledBar = styled.div<{ $color: string }>`
  animation: ${spin} 1.1s linear infinite;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  height: 26px;
  width: 7px;
`;

const pulse = keyframes`
  0%,
  100% {
    transform: scale(0.6);
  }
  50% {
    transform: scale(1);
  }
`;

const CurvedDot = styled.div<{ $color: string }>`
  animation: ${pulse} 1s ease-in-out infinite;
  background: ${({ $color }) => $color};
  border-radius: 50%;
  height: 20px;
  width: 20px;
`;

const JaggedSquare = styled.div<{ $color: string }>`
  animation: ${spin} 1.2s linear infinite;
  background: ${({ $color }) => $color};
  height: 18px;
  width: 18px;
`;

const bounce = keyframes`
  0%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
`;

const WavyDots = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`;

const WavyDot = styled.div<{ $color: string; $delay: number }>`
  animation: ${bounce} 1s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  background: ${({ $color }) => $color};
  border-radius: 50%;
  height: 10px;
  width: 10px;
`;
